import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Activity,
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Lock,
  ShieldCheck,
  Sparkles,
  Headphones,
  Gauge,
  Database,
  AlertTriangle,
  Eye,
  ListChecks,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMG =
  "https://images.unsplash.com/photo-1714899984906-a3e8f43025a0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxkYXJrJTIwZ2VvbWV0cmljJTIwM2QlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3Nzg4NDQzM3ww&ixlib=rb-4.1.0&q=85";
const SOLUTION_IMG =
  "https://images.unsplash.com/photo-1762279388988-3f8abcc7dca2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxkYXJrJTIwZGF0YSUyMHZpc3VhbGl6YXRpb24lMjBhYnN0cmFjdHxlbnwwfHx8fDE3Nzc4ODQ0MzN8MA&ixlib=rb-4.1.0&q=85";
const PROBLEM_IMG =
  "https://images.unsplash.com/photo-1682706841281-f723c5bfcd83?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxoZWFsdGhjYXJlJTIwZGF0YSUyMGFic3RyYWN0JTIwZGFya3xlbnwwfHx8fDE3Nzc4ODQ0MzN8MA&ixlib=rb-4.1.0&q=85";
const TEXTURE_IMG =
  "https://images.unsplash.com/photo-1583444058189-275f9039e32d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbmF2eSUyMGFic3RyYWN0JTIwdGV4dHVyZXxlbnwwfHx8fDE3Nzc4ODQ0MjB8MA&ixlib=rb-4.1.0&q=85";

/* ---------- NAVIGATION ---------- */
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const onScroll = useCallback(() => setScrolled(window.scrollY > 16), []);
  useEffect(() => {
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  const links = [
    { href: "#platform", label: "Platform" },
    { href: "#how-it-works", label: "How it works" },
    { href: "#security", label: "Security" },
    { href: "#faq", label: "FAQ" },
  ];

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#0A0A12]/85 backdrop-blur-xl border-b border-[#2C5F8A]/20"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-5 flex items-center justify-between">
        <a
          href="#top"
          className="flex items-center gap-3 group"
          data-testid="nav-logo"
        >
          <img
            src="/nq-logo.png"
            alt="Nova Quantum Labs"
            className="h-10 w-auto select-none"
            draggable="false"
          />
          <span className="font-display text-[17px] tracking-tight text-white">
            Nova Quantum Labs
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-[13px] tracking-wide text-[#94A3B8] hover:text-white transition-colors"
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <a
          href="#request-demo"
          className="nq-btn-primary text-sm"
          data-testid="nav-request-demo"
        >
          Request Demo <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </header>
  );
};

/* ---------- HERO ---------- */
const HeroBackground = () => (
  <div className="absolute inset-0">
    <img
      src={HERO_IMG}
      alt="Dark geometric background"
      className="w-full h-full object-cover opacity-40"
    />
    <div className="absolute inset-0 bg-[#0A0A12]/70" />
    <div className="absolute inset-0 nq-hero-glow" />
    <div className="absolute inset-0 nq-grid-bg opacity-50" />
  </div>
);

const HERO_STATS = [
  { v: "100%", l: "Calls reviewed" },
  { v: "HIPAA", l: "Compliant by design" },
  { v: "<24h", l: "Time to insight" },
  { v: "Live", l: "Already deployed" },
];

const HeroStats = () => (
  <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#2C5F8A]/20 border border-[#2C5F8A]/20 rounded-lg overflow-hidden nq-fade-up nq-delay-4 max-w-3xl">
    {HERO_STATS.map((s) => (
      <div key={s.l} className="bg-[#0A0A12]/80 px-5 py-5">
        <div className="font-display text-2xl md:text-3xl text-white">
          {s.v}
        </div>
        <div className="mt-1 text-[11px] uppercase tracking-[0.2em] text-[#64748B] font-mono">
          {s.l}
        </div>
      </div>
    ))}
  </div>
);

const Hero = () => (
  <section
    id="top"
    className="relative min-h-[100svh] overflow-hidden nq-noise"
    data-testid="hero-section"
  >
    <HeroBackground />

    <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-44 pb-28 md:pt-52 md:pb-40">
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-9">
          <div className="flex items-center gap-3 nq-fade-up">
            <span className="w-10 h-px bg-[#2C5F8A]" />
            <span className="eyebrow">QA Intelligence Platform · Healthcare</span>
          </div>

          <h1
            className="font-display font-light text-white tracking-tight mt-7 text-[44px] sm:text-6xl md:text-7xl leading-[1.02] nq-fade-up nq-delay-1"
            data-testid="hero-headline"
          >
            Your call center reviews{" "}
            <span className="text-[#C0392B] italic font-normal">3%</span> of calls.
            <br className="hidden md:block" />
            <span className="text-white/95">We review </span>
            <span className="font-normal italic text-white">100%.</span>
          </h1>

          <p
            className="mt-8 max-w-2xl text-[#94A3B8] text-lg md:text-xl font-light leading-relaxed nq-fade-up nq-delay-2"
            data-testid="hero-subheadline"
          >
            The QA Intelligence Platform by Nova Quantum Labs is an AI-powered
            quality assurance system built exclusively for healthcare contact
            centers. HIPAA-compliant. Automated. Deployed and running.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4 nq-fade-up nq-delay-3">
            <a
              href="#request-demo"
              className="nq-btn-primary nq-pulse-glow"
              data-testid="hero-cta"
            >
              Request a private demo
              <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="#platform"
              className="nq-btn-secondary"
              data-testid="hero-secondary-cta"
            >
              See the platform
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>

          <HeroStats />
        </div>
      </div>

      <div className="absolute bottom-8 left-6 md:left-12 lg:left-16 hidden md:flex items-center gap-3 text-[#64748B] text-xs font-mono tracking-[0.18em] uppercase">
        <span className="w-8 h-px bg-[#64748B]" />
        Scroll
      </div>
    </div>
  </section>
);

/* ---------- LOGO MARQUEE ---------- */
const Marquee = () => {
  const items = [
    "Behavioral Health Networks",
    "Multispecialty Clinics",
    "Tele-Triage Operations",
    "Payer Member Services",
    "Revenue Cycle BPOs",
    "Pharmacy Benefit Managers",
    "Patient Access Centers",
  ];
  const row = [
    ...items.map((t) => ({ id: `first-${t}`, t })),
    ...items.map((t) => ({ id: `second-${t}`, t })),
  ];
  return (
    <section className="relative overflow-hidden border-y border-[#2C5F8A]/20 py-7 bg-[#0A0A12]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 mb-4">
        <div className="eyebrow">Built for the operations that can't get it wrong</div>
      </div>
      <div className="nq-marquee">
        {row.map(({ id, t }) => (
          <div
            key={id}
            className="flex items-center gap-10 px-8 text-[#94A3B8] font-display text-2xl tracking-tight whitespace-nowrap"
          >
            <span>{t}</span>
            <span className="text-[#2C5F8A]">◆</span>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ---------- PROBLEM / SOLUTION ---------- */
const PROBLEM_POINTS = [
  "97% of calls go un-audited",
  "Sampling bias hides systemic risk",
  "Agent feedback lags by weeks",
  "Audit trails are reconstructed, not recorded",
];

const SOLUTION_POINTS = [
  { i: <Headphones className="w-4 h-4" />, t: "Automated transcription & speaker separation" },
  { i: <ListChecks className="w-4 h-4" />, t: "Rubric scoring against your QA scorecard" },
  { i: <ShieldCheck className="w-4 h-4" />, t: "PHI-safe pipelines, BAA-backed" },
  { i: <Gauge className="w-4 h-4" />, t: "Live dashboards for ops & compliance" },
];

const ProblemCard = () => (
  <div className="nq-card col-span-12 md:col-span-5 p-8 md:p-10 relative overflow-hidden">
    <div
      className="absolute inset-0 opacity-30"
      style={{
        backgroundImage: `url(${PROBLEM_IMG})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        filter: "grayscale(40%)",
      }}
    />
    <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A2E]/40 via-[#1A1A2E]/85 to-[#1A1A2E]" />
    <div className="relative">
      <div className="flex items-center gap-2 text-[#C0392B] text-xs font-mono tracking-[0.2em] uppercase">
        <AlertTriangle className="w-4 h-4" /> Status quo
      </div>
      <div className="mt-8 font-display text-white text-[110px] md:text-[140px] leading-none tracking-tighter">
        <span className="text-[#C0392B]">3</span>
        <span className="text-white">%</span>
      </div>
      <div className="mt-4 font-display text-2xl text-white">
        of calls reviewed by a human team.
      </div>
      <ul className="mt-7 space-y-3 text-[#94A3B8]">
        {PROBLEM_POINTS.map((t) => (
          <li key={t} className="flex items-start gap-3 text-[15px]">
            <span className="mt-2 w-1 h-1 rounded-full bg-[#C0392B]" />
            {t}
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const SolutionCard = () => (
  <div className="nq-card col-span-12 md:col-span-7 p-8 md:p-10 relative overflow-hidden">
    <div
      className="absolute inset-0 opacity-25"
      style={{
        backgroundImage: `url(${SOLUTION_IMG})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    />
    <div className="absolute inset-0 bg-gradient-to-tr from-[#1A1A2E] via-[#1A1A2E]/80 to-[#1A1A2E]/30" />
    <div className="relative">
      <div className="flex items-center gap-2 text-[#2C5F8A] text-xs font-mono tracking-[0.2em] uppercase">
        <Sparkles className="w-4 h-4" /> Nova Quantum Labs
      </div>
      <div className="mt-8 font-display text-white text-[110px] md:text-[160px] leading-none tracking-tighter">
        <span>100</span>
        <span className="text-[#2C5F8A]">%</span>
      </div>
      <div className="mt-4 font-display text-2xl md:text-3xl text-white max-w-xl leading-snug">
        Every call. Every agent. Every shift —
        scored, surfaced, and audit-ready.
      </div>
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {SOLUTION_POINTS.map((it) => (
          <div
            key={it.t}
            className="flex items-start gap-3 text-[#F8FAFC]/90 text-[14px]"
          >
            <span className="mt-0.5 text-[#2C5F8A]">{it.i}</span>
            <span className="leading-relaxed">{it.t}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const ProblemSolution = () => (
  <section id="platform" className="relative py-24 md:py-32" data-testid="problem-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
      <div className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-14">
        <div className="col-span-12 md:col-span-7">
          <div className="eyebrow">The blind spot</div>
          <h2 className="font-display font-light text-white text-3xl sm:text-4xl md:text-5xl tracking-tight mt-4 leading-[1.08]">
            Manual QA was designed for an era when
            <span className="italic text-[#C0392B]"> sampling</span> was good enough.
            Healthcare is no longer that era.
          </h2>
        </div>
        <div className="col-span-12 md:col-span-5 md:pl-10">
          <p className="text-[#94A3B8] text-base md:text-lg leading-relaxed font-light">
            Reviewing three calls in every hundred leaves ninety-seven untouched —
            unaudited PHI exposure, missed clinical disclosures, untracked agent
            drift. The math has never been on your side.
          </p>
        </div>
      </div>

      {/* Bento */}
      <div className="grid grid-cols-12 gap-6 md:gap-8">
        <ProblemCard />
        <SolutionCard />
      </div>
    </div>
  </section>
);

/* ---------- FEATURES ---------- */
const Features = () => {
  const items = [
    {
      icon: <Headphones className="w-5 h-5" />,
      kicker: "Capture",
      title: "Every call ingested in near real time",
      body:
        "Connects to your existing CCaaS, dialer, or recording vendor. No agent intervention. No new headsets. No new workflow.",
    },
    {
      icon: <ListChecks className="w-5 h-5" />,
      kicker: "Score",
      title: "Your scorecard, evaluated by AI",
      body:
        "Brings your existing QA rubric — clinical disclaimers, identity verification, empathy, resolution — and applies it consistently to 100% of conversations.",
    },
    {
      icon: <Eye className="w-5 h-5" />,
      kicker: "Surface",
      title: "Risk you would have never sampled",
      body:
        "Anomalies, escalations, and PHI mishandling float to the top. Your QA team coaches outliers — not random rows in a spreadsheet.",
    },
    {
      icon: <Database className="w-5 h-5" />,
      kicker: "Defend",
      title: "Always audit-ready",
      body:
        "Every score, every transcript, every reviewer override is timestamped and exportable. When auditors ask, you answer in minutes.",
    },
  ];

  return (
    <section className="relative py-24 md:py-32 bg-[#0A0A12]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-12 gap-6 mb-14">
          <div className="col-span-12 md:col-span-8">
            <div className="eyebrow">The platform</div>
            <h2 className="font-display font-light text-white text-3xl sm:text-4xl md:text-5xl tracking-tight mt-4 leading-[1.08]">
              An end-to-end QA system —
              <span className="italic"> already deployed</span>, already running,
              already auditing calls in production.
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {items.map((it, idx) => (
            <article
              key={it.title}
              className="nq-card p-8 md:p-10 group"
              data-testid={`feature-card-${idx}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-[#2C5F8A]">
                  <span className="w-9 h-9 rounded-md border border-[#2C5F8A]/40 flex items-center justify-center bg-[#0A0A12]">
                    {it.icon}
                  </span>
                  <span className="font-mono text-[11px] tracking-[0.22em] uppercase">
                    {it.kicker}
                  </span>
                </div>
                <span className="font-mono text-[#64748B] text-xs">
                  0{idx + 1}
                </span>
              </div>
              <h3 className="font-display text-2xl md:text-3xl text-white mt-7 leading-snug max-w-md">
                {it.title}
              </h3>
              <p className="mt-4 text-[#94A3B8] leading-relaxed font-light">
                {it.body}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- HOW IT WORKS ---------- */
const HowItWorks = () => {
  const steps = [
    {
      n: "01",
      t: "Connect",
      d: "We integrate with your call recording or CCaaS in days, not quarters. PHI never leaves your control boundary.",
    },
    {
      n: "02",
      t: "Transcribe & redact",
      d: "Speech-to-text with diarization runs against 100% of recordings. PHI is detected and handled per your retention policy.",
    },
    {
      n: "03",
      t: "Score against your rubric",
      d: "We map your scorecard into the platform once. From then on, every call is scored automatically and consistently.",
    },
    {
      n: "04",
      t: "Coach, defend, repeat",
      d: "Supervisors coach with evidence. Compliance pulls audit packets on demand. Leaders see operational drift before it costs.",
    },
  ];
  return (
    <section
      id="how-it-works"
      className="relative py-24 md:py-32"
      data-testid="how-it-works-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-12 gap-6 mb-16 items-end">
          <div className="col-span-12 md:col-span-7">
            <div className="eyebrow">How it works</div>
            <h2 className="font-display font-light text-white text-3xl sm:text-4xl md:text-5xl tracking-tight mt-4 leading-[1.08]">
              Four steps from
              <span className="italic"> sampling </span>
              to <span className="text-[#2C5F8A]">total visibility.</span>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-[#2C5F8A]/20 border border-[#2C5F8A]/20 rounded-xl overflow-hidden">
          {steps.map((s) => (
            <div
              key={s.n}
              className="bg-[#0A0A12] p-8 md:p-10 group hover:bg-[#10101e] transition-colors duration-500"
            >
              <div className="font-mono text-[#C0392B] text-xs tracking-[0.24em]">
                STEP {s.n}
              </div>
              <h3 className="mt-5 font-display text-2xl text-white leading-snug">
                {s.t}
              </h3>
              <div className="mt-4 w-10 h-px bg-[#2C5F8A] group-hover:w-16 transition-all duration-500" />
              <p className="mt-5 text-[#94A3B8] text-[15px] leading-relaxed font-light">
                {s.d}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- TRUST / COMPLIANCE ---------- */
const Compliance = () => (
  <section
    id="security"
    className="relative py-24 md:py-32 overflow-hidden"
    data-testid="security-section"
  >
    <div className="absolute inset-0">
      <img
        src={TEXTURE_IMG}
        alt=""
        className="w-full h-full object-cover opacity-30"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A12] via-[#0A0A12]/85 to-[#0A0A12]" />
    </div>
    <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
      <div className="grid grid-cols-12 gap-10">
        <div className="col-span-12 md:col-span-5">
          <div className="eyebrow">Security & compliance</div>
          <h2 className="font-display font-light text-white text-3xl sm:text-4xl md:text-5xl tracking-tight mt-4 leading-[1.08]">
            HIPAA-grade,
            <span className="italic"> by architecture</span> — not by checklist.
          </h2>
          <p className="mt-6 text-[#94A3B8] font-light leading-relaxed">
            We do not bolt compliance onto a generic AI stack. The QA Intelligence
            Platform was designed from day one for the realities of PHI, BAAs,
            and clinical accountability.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {["HIPAA-aligned", "BAA-backed", "Encrypted at rest & in transit", "Role-based access"].map(
              (b) => (
                <span
                  key={b}
                  className="px-3 py-1.5 rounded-full border border-[#2C5F8A]/40 text-[12px] text-[#F8FAFC]/85 bg-[#1A1A2E]/60 font-mono tracking-wide"
                >
                  {b}
                </span>
              )
            )}
          </div>
        </div>

        <div className="col-span-12 md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {[
            {
              icon: <ShieldCheck className="w-5 h-5" />,
              t: "PHI handling",
              d: "Detection, redaction, and policy-aligned retention across every transcript and audio asset.",
            },
            {
              icon: <Lock className="w-5 h-5" />,
              t: "Encryption",
              d: "AES-256 at rest, TLS 1.2+ in transit, customer-isolated key scopes.",
            },
            {
              icon: <Activity className="w-5 h-5" />,
              t: "Auditable",
              d: "Immutable scoring records and full chain-of-custody on every reviewer decision.",
            },
            {
              icon: <Database className="w-5 h-5" />,
              t: "Data residency",
              d: "Deploy in your tenant or ours. Your PHI, your boundary, your governance.",
            },
          ].map((c) => (
            <div key={c.t} className="nq-card p-7">
              <div className="w-10 h-10 rounded-md bg-[#0A0A12] border border-[#2C5F8A]/40 flex items-center justify-center text-[#2C5F8A]">
                {c.icon}
              </div>
              <h3 className="mt-5 font-display text-xl text-white">{c.t}</h3>
              <p className="mt-2 text-[#94A3B8] font-light text-[14.5px] leading-relaxed">
                {c.d}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ---------- DEMO FORM ---------- */
const DEMO_BULLETS = [
  "Tailored walkthrough on healthcare-specific scorecards",
  "Live look at the deployed platform — not a slide deck",
  "Implementation timeline and BAA process explained",
];

const DemoIntro = () => (
  <div className="col-span-12 md:col-span-5">
    <div className="eyebrow">Request a demo</div>
    <h2 className="font-display font-light text-white text-3xl sm:text-4xl md:text-5xl tracking-tight mt-4 leading-[1.08]">
      See <span className="italic">100% review</span> running on your own
      call patterns.
    </h2>
    <p className="mt-6 text-[#94A3B8] font-light leading-relaxed max-w-md">
      Tell us a bit about your operation. A senior member of the
      Nova Quantum Labs team will reach out — no junior SDR, no automated
      cadence — to schedule a private walkthrough.
    </p>
    <div className="mt-10 space-y-4 text-[#94A3B8] text-[14.5px]">
      {DEMO_BULLETS.map((t) => (
        <div key={t} className="flex items-start gap-3">
          <CheckCircle2 className="w-4 h-4 text-[#2C5F8A] mt-1 shrink-0" />
          <span className="leading-relaxed">{t}</span>
        </div>
      ))}
    </div>
  </div>
);

const DemoSuccess = () => (
  <div className="text-center py-12" data-testid="demo-form-success">
    <div className="mx-auto w-12 h-12 rounded-full border border-[#2C5F8A]/50 flex items-center justify-center text-[#2C5F8A]">
      <CheckCircle2 className="w-6 h-6" />
    </div>
    <h3 className="mt-6 font-display text-3xl text-white">
      Request received.
    </h3>
    <p className="mt-4 text-[#94A3B8] max-w-md mx-auto leading-relaxed font-light">
      Thank you. A member of the Nova Quantum Labs team will be in
      touch within one business day to schedule your private
      walkthrough.
    </p>
  </div>
);

const VolumeSelect = ({ value, onChange }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger
      className="nq-input h-auto py-[14px]"
      data-testid="demo-form-volume"
    >
      <SelectValue placeholder="Select a range" />
    </SelectTrigger>
    <SelectContent className="bg-[#1A1A2E] border-[#2C5F8A]/40 text-white">
      <SelectItem value="<10k">Less than 10,000</SelectItem>
      <SelectItem value="10k-50k">10,000 – 50,000</SelectItem>
      <SelectItem value="50k-250k">50,000 – 250,000</SelectItem>
      <SelectItem value="250k-1M">250,000 – 1M</SelectItem>
      <SelectItem value=">1M">More than 1M</SelectItem>
    </SelectContent>
  </Select>
);

const DemoFormFields = ({ form, update, onVolumeChange, submitting, onSubmit }) => (
  <form
    onSubmit={onSubmit}
    className="grid grid-cols-1 sm:grid-cols-2 gap-5"
    data-testid="demo-form"
  >
    <Field label="Full name" required>
      <input
        type="text"
        className="nq-input"
        placeholder="Dr. Jane Whitfield"
        value={form.name}
        onChange={update("name")}
        data-testid="demo-form-name"
      />
    </Field>
    <Field label="Work email" required>
      <input
        type="email"
        className="nq-input"
        placeholder="jane@yourhealth.com"
        value={form.email}
        onChange={update("email")}
        data-testid="demo-form-email"
      />
    </Field>
    <Field label="Company" required>
      <input
        type="text"
        className="nq-input"
        placeholder="Yourhealth Networks"
        value={form.company}
        onChange={update("company")}
        data-testid="demo-form-company"
      />
    </Field>
    <Field label="Role">
      <input
        type="text"
        className="nq-input"
        placeholder="VP, Compliance"
        value={form.role}
        onChange={update("role")}
        data-testid="demo-form-role"
      />
    </Field>
    <Field label="Monthly call volume" className="sm:col-span-2">
      <VolumeSelect value={form.call_volume} onChange={onVolumeChange} />
    </Field>
    <Field label="What would you like to see?" className="sm:col-span-2">
      <textarea
        rows={4}
        className="nq-input resize-none"
        placeholder="A few sentences on your QA process today and what you'd like to evaluate."
        value={form.message}
        onChange={update("message")}
        data-testid="demo-form-message"
      />
    </Field>

    <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
      <p className="text-[12px] text-[#64748B] font-mono leading-relaxed max-w-md">
        By submitting, you agree to be contacted by Nova Quantum Labs
        regarding the QA Intelligence Platform.
      </p>
      <button
        type="submit"
        disabled={submitting}
        className="nq-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid="demo-form-submit"
      >
        {submitting ? "Sending…" : "Request private demo"}
        {!submitting && <ArrowRight className="w-4 h-4" />}
      </button>
    </div>
  </form>
);

const DemoForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    call_volume: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const onVolumeChange = (v) => setForm((f) => ({ ...f, call_volume: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) {
      toast.error("Please fill in name, email and company.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/demo-request`, form);
      setSubmitted(true);
      toast.success("Request received. We'll be in touch within one business day.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or email us directly.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="request-demo"
      className="relative py-24 md:py-32 bg-[#0A0A12]"
      data-testid="demo-form-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-12 gap-10">
          <DemoIntro />
          <div className="col-span-12 md:col-span-7">
            <div className="nq-card p-8 md:p-10 relative">
              {submitted ? (
                <DemoSuccess />
              ) : (
                <DemoFormFields
                  form={form}
                  update={update}
                  onVolumeChange={onVolumeChange}
                  submitting={submitting}
                  onSubmit={onSubmit}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, required, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="block text-[12px] font-mono uppercase tracking-[0.18em] text-[#94A3B8] mb-2">
      {label}
      {required && <span className="text-[#C0392B]"> *</span>}
    </span>
    {children}
  </label>
);

/* ---------- FAQ ---------- */
const FAQ = () => {
  const items = [
    {
      q: "Are you actually HIPAA compliant, or just 'aligned'?",
      a: "We operate under signed Business Associate Agreements with our healthcare clients and treat PHI with the controls those agreements require — encryption, role-based access, audit logs, redaction, and retention controls. We're happy to walk through our security posture under NDA.",
    },
    {
      q: "Will this replace our QA team?",
      a: "No. The platform replaces sampling — not judgment. Your QA analysts stop scoring random calls and start coaching the outliers our system surfaces. Most clients see analyst capacity effectively double in the first 90 days.",
    },
    {
      q: "How long does a deployment take?",
      a: "For most healthcare contact centers, the platform is ingesting and scoring calls within 2–4 weeks of contract execution, including BAA, integration, and rubric mapping.",
    },
    {
      q: "What does it integrate with?",
      a: "Major CCaaS platforms (NICE, Five9, Genesys, Talkdesk, Amazon Connect), recording vendors, and any storage layer that exposes audio or transcript files. If it makes a call, we can score it.",
    },
    {
      q: "Can we keep our existing scorecard?",
      a: "Yes. Your scorecard is the input. We do not impose a generic rubric — we evaluate against the criteria your compliance and operations teams already agree on.",
    },
  ];
  return (
    <section id="faq" className="py-24 md:py-32" data-testid="faq-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-4">
            <div className="eyebrow">Frequently asked</div>
            <h2 className="font-display font-light text-white text-3xl sm:text-4xl md:text-5xl tracking-tight mt-4 leading-[1.08]">
              Questions
              <span className="italic"> we get</span> from healthcare leaders.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-8">
            <Accordion type="single" collapsible className="w-full">
              {items.map((it, i) => (
                <AccordionItem
                  key={it.q}
                  value={it.q}
                  className="border-b border-[#2C5F8A]/20"
                >
                  <AccordionTrigger
                    className="text-left text-white font-display text-xl md:text-2xl py-6 hover:no-underline"
                    data-testid={`faq-q-${i}`}
                  >
                    {it.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#94A3B8] font-light leading-relaxed pb-7 text-[15.5px]">
                    {it.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- CTA STRIP ---------- */
const CtaStrip = () => (
  <section className="relative py-20 border-y border-[#2C5F8A]/20">
    <div className="absolute inset-0 nq-grid-bg opacity-40" />
    <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
      <div>
        <div className="eyebrow">Ready when you are</div>
        <h3 className="font-display text-white text-3xl md:text-4xl mt-3 leading-tight max-w-2xl">
          Stop reviewing 3% of calls.
          <span className="italic text-[#94A3B8]"> Start defending all of them.</span>
        </h3>
      </div>
      <a href="#request-demo" className="nq-btn-primary" data-testid="cta-strip-button">
        Request your demo <ArrowRight className="w-4 h-4" />
      </a>
    </div>
  </section>
);

/* ---------- FOOTER ---------- */
const Footer = () => (
  <footer
    className="relative bg-[#0A0A12] pt-20 pb-10"
    data-testid="site-footer"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
      <div className="grid grid-cols-12 gap-10">
        <div className="col-span-12 md:col-span-6">
          <div className="flex items-center gap-3">
            <img
              src="/nq-logo.png"
              alt="Nova Quantum Labs"
              className="h-10 w-auto select-none"
              draggable="false"
            />
            <span className="font-display text-white text-xl">
              Nova Quantum Labs
            </span>
          </div>
          <p className="mt-6 text-[#94A3B8] font-light max-w-md leading-relaxed">
            The QA Intelligence Platform — AI-powered quality assurance built
            exclusively for healthcare contact centers. HIPAA-compliant.
            Automated. Deployed and running.
          </p>
        </div>
        <div className="col-span-6 md:col-span-3">
          <div className="eyebrow">Platform</div>
          <ul className="mt-5 space-y-3 text-[#94A3B8] text-[14.5px]">
            <li><a href="#platform" className="hover:text-white transition-colors">How it works</a></li>
            <li><a href="#security" className="hover:text-white transition-colors">Security</a></li>
            <li><a href="#faq" className="hover:text-white transition-colors">FAQ</a></li>
          </ul>
        </div>
        <div className="col-span-6 md:col-span-3">
          <div className="eyebrow">Company</div>
          <ul className="mt-5 space-y-3 text-[#94A3B8] text-[14.5px]">
            <li><a href="#request-demo" className="hover:text-white transition-colors">Request demo</a></li>
            <li><a href="mailto:hello@novaquantumlabs.com" className="hover:text-white transition-colors">hello@novaquantumlabs.com</a></li>
          </ul>
        </div>
      </div>
      <div className="nq-divider mt-16" />
      <div className="mt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <p className="text-[12px] font-mono tracking-[0.18em] uppercase text-[#64748B]">
          © {new Date().getFullYear()} Nova Quantum Labs LLC · All rights reserved
        </p>
        <p className="text-[12px] font-mono tracking-[0.18em] uppercase text-[#64748B]">
          Built for healthcare. Audit-ready by design.
        </p>
      </div>
    </div>
  </footer>
);

/* ---------- PAGE ---------- */
const Landing = () => {
  const ref = useRef(null);
  return (
    <main
      ref={ref}
      className="min-h-screen bg-[#0A0A12] text-white selection:bg-[#C0392B]/40"
      data-testid="landing-page"
    >
      <Navigation />
      <Hero />
      <Marquee />
      <ProblemSolution />
      <Features />
      <HowItWorks />
      <Compliance />
      <CtaStrip />
      <DemoForm />
      <FAQ />
      <Footer />
    </main>
  );
};

export default Landing;
